import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Check as ApproveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { ItemInventory, TRANSACTION_TYPES } from '../types/inventory.types';
import { inventoryApi } from '../api/inventoryApi';

const InventoryDetail: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState<boolean>(true);
  const [transaction, setTransaction] = useState<ItemInventory | null>(null);
  const [showApproveDialog, setShowApproveDialog] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  // Get transaction type configuration
  const typeConfig = TRANSACTION_TYPES.find(t => t.type === transaction?.type);

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'warning';
      case 'APPROVED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  // Load transaction
  const loadTransaction = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await inventoryApi.getInventoryTransactionById(id, true);
      setTransaction(data);
    } catch (error) {
      console.error('Error loading transaction:', error);
      enqueueSnackbar('Failed to load transaction', { variant: 'error' });
      navigate('/inventory/transactions');
    } finally {
      setLoading(false);
    }
  };

  // Handle approve
  const handleApprove = async () => {
    if (!id) return;

    try {
      setActionLoading(true);
      await inventoryApi.approveInventoryTransaction(id);
      enqueueSnackbar('Transaction approved successfully', { variant: 'success' });
      setShowApproveDialog(false);
      loadTransaction();
    } catch (error: any) {
      console.error('Error approving transaction:', error);
      enqueueSnackbar(error.message || 'Failed to approve transaction', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = async () => {
    if (!id) return;

    try {
      setActionLoading(true);
      await inventoryApi.cancelInventoryTransaction(id);
      enqueueSnackbar('Transaction cancelled successfully', { variant: 'success' });
      setShowCancelDialog(false);
      loadTransaction();
    } catch (error: any) {
      console.error('Error cancelling transaction:', error);
      enqueueSnackbar(error.message || 'Failed to cancel transaction', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!id) return;

    try {
      setActionLoading(true);
      await inventoryApi.deleteInventoryTransaction(id);
      enqueueSnackbar('Transaction deleted successfully', { variant: 'success' });
      navigate('/inventory/transactions');
    } catch (error: any) {
      console.error('Error deleting transaction:', error);
      enqueueSnackbar(error.message || 'Failed to delete transaction', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle edit
  const handleEdit = () => {
    navigate(`/inventory/${id}/edit`);
  };

  // Handle back
  const handleBack = () => {
    navigate('/inventory/transactions');
  };

  // Load transaction on mount
  useEffect(() => {
    loadTransaction();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!transaction) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="textSecondary">
          Transaction not found
        </Typography>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Inventory
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h4">
            {typeConfig?.label || transaction.type} - {transaction.transactionNumber}
          </Typography>
          <Chip
            label={transaction.status}
            color={getStatusColor(transaction.status) as any}
          />
        </Box>
        <Box display="flex" gap={2}>
          {transaction.status === 'DRAFT' && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => setShowApproveDialog(true)}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<CancelIcon />}
                onClick={() => setShowCancelDialog(true)}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>
        </Box>
      </Box>

      {/* Transaction Information */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Transaction Information
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="textSecondary" display="block">
              Transaction Number
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {transaction.transactionNumber}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="textSecondary" display="block">
              Transaction Date
            </Typography>
            <Typography variant="body1">
              {formatDate(transaction.transactionDate)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="textSecondary" display="block">
              Transaction Type
            </Typography>
            <Typography variant="body1">
              {typeConfig?.label || transaction.type}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="textSecondary" display="block">
              Status
            </Typography>
            <Chip
              label={transaction.status}
              color={getStatusColor(transaction.status) as any}
              size="small"
            />
          </Grid>

          {transaction.fromLocation && (
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary" display="block">
                From Location
              </Typography>
              <Typography variant="body1">
                {transaction.fromLocation.name} ({transaction.fromLocation.code})
              </Typography>
            </Grid>
          )}

          {transaction.toLocation && (
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary" display="block">
                To Location
              </Typography>
              <Typography variant="body1">
                {transaction.toLocation.name} ({transaction.toLocation.code})
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="textSecondary" display="block">
              Total Stock Value
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {transaction.totalStockValue}
            </Typography>
          </Grid>

          {transaction.remarks && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary" display="block">
                Remarks
              </Typography>
              <Typography variant="body1">
                {transaction.remarks}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Item Details */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Item Details
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {transaction.details && transaction.details.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Batch Number</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Cost Price</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transaction.details.map((detail, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {detail.item?.itemName || 'Unknown Item'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {detail.item?.itemCode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {detail.batchNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {detail.expiryDate ? formatDate(detail.expiryDate) : 'No expiry'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={detail.mode}
                        color={detail.mode === 'CREDIT' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {detail.quantity.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {detail.costPrice}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {detail.totalItemValue}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {detail.balance.toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Total Row */}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell colSpan={6} sx={{ fontWeight: 'bold' }}>
                    TOTAL
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {transaction.totalStockValue}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="textSecondary" textAlign="center" py={4}>
            No item details available
          </Typography>
        )}
      </Paper>

      {/* Audit Information */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Audit Information
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="textSecondary" display="block">
              Created At
            </Typography>
            <Typography variant="body2">
              {formatDate(transaction.createdAt)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="textSecondary" display="block">
              Updated At
            </Typography>
            <Typography variant="body2">
              {formatDate(transaction.updatedAt)}
            </Typography>
          </Grid>

          {transaction.approvedBy && transaction.approvedAt && (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Approved By
                </Typography>
                <Typography variant="body2">
                  {transaction.approvedBy}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Approved At
                </Typography>
                <Typography variant="body2">
                  {formatDate(transaction.approvedAt)}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onClose={() => setShowApproveDialog(false)}>
        <DialogTitle>Approve Transaction</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve this transaction? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApproveDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleApprove} 
            color="success" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>Cancel Transaction</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this transaction?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)} disabled={actionLoading}>
            No
          </Button>
          <Button 
            onClick={handleCancel} 
            color="warning" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Transaction</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will permanently delete the transaction. This cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete this transaction?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryDetail;
